import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { formatDate, formatDateAt } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import { useTheme } from "simplydo/react/theme";
import {
  Button,
  Divider,
  Form,
  Icon,
  Label,
  Message,
  Popup,
  Segment,
} from "simplydo/react/ui";

import util from "utils/utils";

import { useAssessmentEditor } from "components/ideas/hooks/useAssessmentEditor";
import { PlaceholderItem, Sticky, TooltipButton } from "components/lib/UI";

import { useChildIdea } from "../hooks/useIdea";

type IdeaSection = NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number];
type IdeaField = IdeaSection["fields"][number];

type AssessorToolbarProps = {
  activeItemId: string | null;
  isEditing?: boolean;
};

export const AssessorToolbar = ({
  isEditing,
  activeItemId,
}: AssessorToolbarProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [idea] = useChildIdea();

  const {
    isAssessingSelectedRound,
    isAssessmentLoading,
    assessmentState,
    updateAssessment,
    submitAssessment,
    assessmentIsSubmissable,
  } = useAssessmentEditor(idea);

  const isSubmitted = assessmentState?.isSubmitted;
  const { conflictOfInterest = {} } = assessmentState || {};
  const { hasConflictOfInterest } = conflictOfInterest;
  const hasCompletedConflictDeclaration = hasConflictOfInterest !== undefined;

  const assessmentRound = useMemo(() => {
    return idea?.ownerIdeaTemplate?.assessments?.rounds?.find(
      (ar) => ar.id === (assessmentState?.assessmentRoundId || "legacy"),
    );
  }, [assessmentState?.assessmentRoundId, idea?.ownerIdeaTemplate]);

  const assessmentIsClosed =
    (assessmentState?.assessmentRoundId || "legacy") !== "legacy" ?
      assessmentRound?.assessmentClosed
    : idea?.ownerIdeaTemplate?.assessmentClosed;
  const assessmentDeadline =
    (assessmentState?.assessmentRoundId || "legacy") !== "legacy" ?
      assessmentRound?.assessmentDeadline
    : idea?.ownerIdeaTemplate?.assessmentDeadline;

  const allSections: Array<
    Omit<IdeaSection, "fields"> & {
      fields: Array<
        IdeaField & {
          isAssessment?: boolean;
          isAssessable?: boolean;
          isComplete?: boolean;
        }
      >;
    }
  > = useMemo(() => {
    return (
      idea?.ownerIdeaTemplate?.body?.map((section) => ({
        ...section,
        fields: (section.fields ?? []).map((field) => {
          if (section.type === "assessment") {
            return {
              ...field,
              isAssessment: true,
              isAssessable: util.isAssessableField(field),
              isComplete: util.assessmentFieldIsComplete(
                assessmentState?.assessment,
                field,
              ),
            };
          }
          return field;
        }),
      })) ?? []
    );
  }, [idea?.ownerIdeaTemplate?.body, assessmentState]);

  const assessmentStatus = useMemo(() => {
    let assessedFieldCount = 0;
    const assessmentSections: Array<IdeaSection> = [];

    let assessableCount = 1;
    let currentSection: IdeaSection | null = null;
    let currentField: IdeaField | null = null;

    for (let i = 0; i < allSections.length; i++) {
      const section = allSections[i];

      if (
        !section ||
        section.type !== "assessment" ||
        !section.fields ||
        section.fields.length === 0
      ) {
        continue;
      }
      if (
        (section.assessmentRoundId || "legacy") !==
        (assessmentState?.assessmentRoundId || "legacy")
      ) {
        continue;
      }

      for (let j = 0; j < section.fields.length; j++) {
        const field = section.fields[j];
        if (!field) {
          continue;
        }
        if ("isAssessment" in field && field.isAssessment) {
          if (activeItemId === field.id) {
            currentField = field;
            currentSection = section;
          }
        }
        if ("isAssessable" in field && field.isAssessable) {
          assessableCount += 1;

          if ("isComplete" in field && field.isComplete) {
            assessedFieldCount += 1;
          }
        }
      }

      assessmentSections.push(section);
    }
    return {
      currentSection,
      currentField,
      assessedFieldCount,
      assessmentFieldCount: assessableCount - 1,
      assessmentSections,
    };
  }, [allSections, activeItemId, assessmentState?.assessmentRoundId]);

  if (!isAssessingSelectedRound) {
    return null;
  }

  if (isAssessmentLoading) {
    return (
      <Sticky
        top={
          window.location.pathname === `/ideas/${idea._id}` ?
            isEditing ?
              155
            : 110
          : 5
        }
        bottom={undefined}
        style={{
          zIndex: 75,
          marginBottom: 20,
          borderRadius: 5,
          boxShadow: "rgba(0, 0, 0, 0.3) 0px 8px 12px",
        }}
      >
        <Segment
          style={{
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: theme.colorMap?.violet,
          }}
        >
          <PlaceholderItem height={30} style={{ width: 50, opacity: 0.5 }} />
          <PlaceholderItem height={30} style={{ width: 200, opacity: 0.5 }} />
          <PlaceholderItem height={30} style={{ width: 100, opacity: 0.5 }} />
        </Segment>
      </Sticky>
    );
  }

  return (
    <Sticky
      top={
        window.location.pathname === `/ideas/${idea._id}` ?
          isEditing ?
            155
          : 110
        : 5
      }
      bottom={undefined}
      style={{
        zIndex: 75,
        marginBottom: 20,
        borderRadius: 5,
        boxShadow: "rgba(0, 0, 0, 0.3) 0px 8px 12px",
      }}
    >
      <Segment
        attached={
          (
            assessmentIsClosed ||
            !hasCompletedConflictDeclaration ||
            hasConflictOfInterest === true
          ) ?
            "top"
          : undefined
        }
        style={{
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: theme.colorMap?.violet,
        }}
      >
        <div
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {!hasCompletedConflictDeclaration ? null : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                flexWrap: "wrap",
              }}
            >
              {assessmentDeadline ?
                <div className="assessment-tour-step-5">
                  <Popup
                    trigger={
                      <Label color={assessmentIsClosed ? "yellow" : "grey"}>
                        Deadline: {formatDateAt(assessmentDeadline)}
                      </Label>
                    }
                    content={
                      <p>
                        <b>Assessment deadline:</b> Assessment for this{" "}
                        {t("generic.challenge")} has a deadline of{" "}
                        <b>{formatDateAt(assessmentDeadline)}</b>. You cannot
                        edit or submit assessments after this date.
                      </p>
                    }
                  />
                </div>
              : null}

              {(
                !assessmentIsClosed &&
                assessmentState?.conflictOfInterest?.hasConflictOfInterest ===
                  false
              ) ?
                <Label
                  color={
                    (
                      assessmentStatus.assessedFieldCount ===
                      assessmentStatus.assessmentFieldCount
                    ) ?
                      "green"
                    : "grey"
                  }
                >
                  Assessed {assessmentStatus.assessedFieldCount} /{" "}
                  {assessmentStatus.assessmentFieldCount}
                </Label>
              : null}
            </div>
          )}

          {!hasCompletedConflictDeclaration ?
            <h3 style={{ margin: 0, padding: 0 }}>
              Assessment: Conflict of interest
            </h3>
          : <Button.Group compact size="small">
              {assessmentIsClosed ? null : (
                <Button
                  disabled={isSubmitted}
                  onClick={() => {
                    updateAssessment(undefined, [
                      "conflictOfInterest",
                      "hasConflictOfInterest",
                    ]);
                  }}
                  color={hasConflictOfInterest ? "red" : "green"}
                >
                  Conflict of interest: {hasConflictOfInterest ? "Yes" : "No"}
                </Button>
              )}
              {!assessmentIsClosed && !isSubmitted ?
                <Popup
                  trigger={
                    <Button
                      icon
                      onClick={() => {
                        updateAssessment(undefined, [
                          "conflictOfInterest",
                          "hasConflictOfInterest",
                        ]);
                      }}
                    >
                      <Icon name="pencil" />
                    </Button>
                  }
                  content="Edit conflict of interest declaration"
                />
              : null}
            </Button.Group>
          }

          {isSubmitted ?
            <Label color="green">Assessment submitted</Label>
          : hasConflictOfInterest === false && !assessmentIsClosed ?
            <TooltipButton
              size="small"
              content="Submit assessment"
              disabled={!assessmentIsSubmissable}
              tooltip={
                assessmentIsSubmissable ? undefined : (
                  "To submit an assessment you either have to complete all sections of the assessment or declare a conflict of interest"
                )
              }
              compact
              onClick={submitAssessment}
            />
          : null}
        </div>
      </Segment>
      {assessmentIsClosed ?
        <Segment attached>
          <Message warning size="small">
            <p>
              <b>Assessment closed:</b> Assessment is now closed for this{" "}
              {t("generic.challenge")}. You can continue to view your assigned
              ideas and previous assessments. If you think this is in error,
              please contact an administrator.
            </p>
          </Message>
        </Segment>
      : !hasCompletedConflictDeclaration ?
        <Segment attached>
          <p>
            If you have a conflict of interest that would prevent you from
            objectively assessing this {t("generic.idea")} please declare it
            now. If you declare a conflict of interest you can submit the
            assessment without completing it. Once submitted, managers of this{" "}
            {t("generic.challenge")} will be notified. You can edit your
            conflict declaration at any time before submitting the assessment.
          </p>
          <p>
            You must complete the conflict of interest declaration before you
            can view the assessment.
          </p>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Message>
                <Message.Header>
                  <Icon name="check circle" color="green" />
                  No conflict of interest
                </Message.Header>

                <Divider fitted style={{ marginTop: 5, marginBottom: 4 }} />
                <Message.Content>
                  I confirm that I do not have any conflict of interest that
                  would prevent me from objectively assessing this{" "}
                  {t("generic.idea")}.
                  <br />
                  <Button
                    style={{ marginTop: 10 }}
                    content={`Declare no conflict and proceed to assessment`}
                    size="small"
                    onClick={() => {
                      updateAssessment(false, [
                        "conflictOfInterest",
                        "hasConflictOfInterest",
                      ]);
                    }}
                  />
                </Message.Content>
              </Message>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Message>
                <Message.Header>
                  <Icon name="warning sign" color="yellow" />
                  Conflict of interest
                </Message.Header>
                <Divider fitted style={{ marginTop: 5, marginBottom: 4 }} />
                <Message.Content>
                  I have a conflict of interest that would prevent me from
                  objectively assessing this {t("generic.idea")}.
                  <br />
                  <Button
                    size="small"
                    style={{ marginTop: 10 }}
                    content={`I do need to declare a conflict of interest`}
                    onClick={() => {
                      updateAssessment(true, [
                        "conflictOfInterest",
                        "hasConflictOfInterest",
                      ]);
                    }}
                    disabled={isSubmitted}
                  />
                </Message.Content>
              </Message>
            </div>
          </div>
        </Segment>
      : hasConflictOfInterest === false ?
        null
      : <Segment attached>
          <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <p style={{ marginBottom: 8 }}>
              You declared a conflict of interest for this assessment, please
              provide more context below before submitting the assessment.
            </p>
            <Form style={{ marginTop: 0 }}>
              <Form.TextArea
                placeholder="Please provide any further context or information.."
                disabled={isSubmitted}
                value={assessmentState?.conflictOfInterest?.notes || ""}
                onChange={(_e, { value }) =>
                  updateAssessment(value, ["conflictOfInterest", "notes"])
                }
              />
            </Form>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 12,
              }}
            >
              {isSubmitted ? null : (
                <TooltipButton
                  content="Submit assessment with conflict of interest"
                  disabled={!assessmentIsSubmissable}
                  tooltip={
                    assessmentIsSubmissable ? undefined : (
                      "To submit an assessment you either have to complete all sections of the assessment or declare a conflict of interest"
                    )
                  }
                  primary
                  onClick={submitAssessment}
                />
              )}
            </div>
          </div>
        </Segment>
      }
    </Sticky>
  );
};
